/**
 * SA per-demo detail page (B-11; provision→status flow 2026-06-12).
 *
 * Route: /demos/:slug
 *
 * 2026-06-15 control-panel redesign (HoD spec
 * backend/docs/hod_sa_demo_detail_design.md): the flat single-column form-dump
 * is restructured into a control panel — an always-visible header band (env
 * icon + slug + StatusPill + Share-login + login-URL row), a 3-up
 * CollapsibleCard row (System Metrics / Environment Security / Integration
 * Context), a collapsible Users section with search + icon row-actions, and a
 * quarantined Danger Zone (Factory Reset + Terminate, each revealing the
 * existing token-typing confirm form). Layout only — all functionality,
 * handlers, confirm gates, and data-testids preserved.
 *
 * Provisioning state machine (founder intent — see
 * backend/docs/cofounder_brief_sa_provision_demo.md):
 *   - Arriving here right after clicking "Provision Demo" renders a
 *     **Provisioning…** banner. The slug→login indicator is shown but
 *     non-actionable.
 *   - The page polls GET /admin/demo/:slug/status and watches the in-flight
 *     run in useDemoProvisionStore. When the run lands **Ready**, the slug
 *     indicator becomes a clickable link to the tenant's login URL.
 *   - On **Failed**, a Retry re-fires the same provision body.
 *   - No fabricated timer — every state derives from the real POST outcome
 *     and the real status endpoint.
 *
 * The Reset / Delete actions are unchanged but hidden while a provision run is
 * in flight (you can't reset a tenant that isn't seeded yet).
 */
import { useState, useEffect, type FormEvent, type ReactNode } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Server,
  Link as LinkIcon,
  BarChart3,
  ShieldCheck,
  Workflow,
  Users2,
  ListChecks,
  Clock,
  Mail,
  Search,
  AlertTriangle,
} from "lucide-react";
import { TenantUsersTab } from "@/components/admin/tenants/TenantUsersTab";
import { CollapsibleCard } from "@/components/common/CollapsibleCard";
import { Button } from "@/components/common/Button";
import { CopyButton } from "@/components/common/CopyButton";
import { Toggle } from "@/components/common/Toggle";
import { useAdminDemo } from "@/hooks/queries/admin/useAdminDemo";
import { useResetDemo } from "@/hooks/queries/admin/useResetDemo";
import { useDeleteDemo } from "@/hooks/queries/admin/useDeleteDemo";
import { useAdminDemos } from "@/hooks/queries/admin/useAdminDemos";
import {
  useSetTenantMfaRequired,
  useSetTenantDashboardPersona,
} from "@/hooks/queries/admin/useAdminTenants";
import { useToastStore } from "@/stores/useToastStore";
import {
  useDemoProvisionStore,
  type DemoProvisionState,
} from "@/stores/useDemoProvisionStore";

/** Status poll cadence while a provision is in flight (ms). */
const PROVISION_POLL_MS = 3000;

// Per PR #404 — every sandbox shares this admin login (email is unique
// per-tenant, so the same address is reused across sandboxes) plus the fixed
// known DEMO_ADMIN_PASSWORD, with no forced first-login reset. The password is
// a backend-only secret (never in the API response), so the share block below
// surfaces the email + URL for copy-paste and references the shared password.
const DEMO_ADMIN_EMAIL = "demo@certainhr.ai";

/** Unified header/banner state — StatusPill + ProvisioningBanner share it. */
type HeaderState = "ready" | "provisioning" | "failed" | "resetting" | "none";

/**
 * Render a seeded count as "40 of 100" when it is short of expectation, or the
 * bare number when whole. A shortfall in isolation reads as a normal count —
 * pairing it with the expectation is what makes a half-seeded tenant legible.
 */
function seededValue(actual: number, expected: number): string {
  return expected > 0 && actual < expected
    ? `${actual} of ${expected}`
    : String(actual);
}

export default function DemoDetail(): JSX.Element {
  const { slug = "" } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const addToast = useToastStore((s) => s.addToast);

  const run = useDemoProvisionStore((s) => s.runs[slug]);
  const startProvision = useDemoProvisionStore((s) => s.startProvision);

  // ── Derive the unified provisioning state ──────────────────────────────────
  // The store run (the live POST) is authoritative. When there's no run (SA
  // reloaded mid-provision or reopened later) we fall back to the status shape:
  //   exists && last_reset_at && ranking_count>0  ⇒ Ready
  //   exists && !last_reset_at                     ⇒ still Provisioning
  // Status alone can't prove Failed (tenant row is committed before seeding and
  // not rolled back on failure), so absent a store run we never *invent* Failed.
  const status = useAdminDemo(slug);

  let provisionState: DemoProvisionState | null = run?.state ?? null;
  if (provisionState === null && status.data) {
    if (
      status.data.exists &&
      status.data.last_reset_at &&
      status.data.ranking_count > 0
    ) {
      provisionState = "ready";
    }
    // exists && !ready → leave null (treat as a normal, already-provisioned
    // tenant view); we only show the Provisioning banner when a store run is
    // active, so a stale half-state doesn't trap the page in a fake spinner.
  }

  // The reset mutation is part of the unified header machine too: an
  // already-provisioned tenant being factory-reset must read "Resetting", not
  // a contradictory "Ready", until the real mutation resolves.
  const reset = useResetDemo(slug);

  const isProvisioning = run?.state === "provisioning";
  const isFailed = run?.state === "failed";
  const isResetting = reset.isPending;
  const isReady = provisionState === "ready";

  // Single source of truth for both the StatusPill and the ProvisioningBanner,
  // so they can never disagree (HoD §6). Resetting takes precedence over ready
  // but sits below provisioning/failed (those describe a fresh provision run).
  const headerState: HeaderState = isProvisioning
    ? "provisioning"
    : isFailed
      ? "failed"
      : isResetting
        ? "resetting"
        : isReady
          ? "ready"
          : "none";

  // Poll status while provisioning OR resetting (the run/mutation flips state
  // via its promise, but the poll keeps candidate/ranking counts fresh as the
  // re-seed lands).
  useEffect(() => {
    if (!isProvisioning && !isResetting) return;
    const id = window.setInterval(() => {
      void status.refetch();
    }, PROVISION_POLL_MS);
    return () => window.clearInterval(id);
  }, [isProvisioning, isResetting, status]);

  // Refresh status once when a run lands Ready so counts reflect the final seed.
  useEffect(() => {
    if (run?.state === "ready") void status.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.state]);

  // Honest elapsed-seconds counter for the resetting banner — real wall-clock
  // time from when the reset began, NOT a fabricated progress percentage. Starts
  // a 1s tick when the reset goes pending and clears (resetting to 0) otherwise.
  const [resetElapsed, setResetElapsed] = useState(0);
  useEffect(() => {
    if (!isResetting) {
      setResetElapsed(0);
      return;
    }
    const startedAt = Date.now();
    setResetElapsed(0);
    const id = window.setInterval(() => {
      setResetElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => window.clearInterval(id);
  }, [isResetting]);

  // ── Reset / Delete form state ──────────────────────────────────────────────
  const deleteDemo = useDeleteDemo(slug);
  const demoList = useAdminDemos();

  const [resetEmail, setResetEmail] = useState("");
  const [resetFirstName, setResetFirstName] = useState("Demo");
  const [resetLastName, setResetLastName] = useState("Admin");
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetCopyState, setResetCopyState] = useState<"idle" | "copied">(
    "idle",
  );

  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [showResetPanel, setShowResetPanel] = useState(false);
  const [showDeletePanel, setShowDeletePanel] = useState(false);

  // Users-section search (HoD §4.1). Wired into TenantUsersTab.
  const [userQuery, setUserQuery] = useState("");

  useEffect(() => {
    if (status.data?.admin_email && resetEmail === "") {
      setResetEmail(status.data.admin_email);
    }
  }, [status.data?.admin_email, resetEmail]);

  const RESET_TOKEN = `RESET DEMO ${slug}`;
  const DELETE_TOKEN = `DELETE DEMO ${slug}`;

  const resetConfirmOk = resetConfirm === RESET_TOKEN;
  const resetEnabled =
    !reset.isPending && resetConfirmOk && resetEmail.length > 0;

  const deleteConfirmOk = deleteConfirm === DELETE_TOKEN;
  const deleteEnabled = !deleteDemo.isPending && deleteConfirmOk;

  function onResetSubmit(e: FormEvent) {
    e.preventDefault();
    if (!resetEnabled) return;
    reset.mutate({
      admin_email: resetEmail,
      admin_first_name: resetFirstName,
      admin_last_name: resetLastName,
      confirm: RESET_TOKEN,
    });
  }

  function onDeleteSubmit(e: FormEvent) {
    e.preventDefault();
    if (!deleteEnabled) return;
    deleteDemo.mutate(
      { confirm: DELETE_TOKEN },
      {
        onSuccess: () => {
          addToast(`Demo "${slug}" deleted.`, "success");
          demoList.refetch();
          navigate("/demos");
        },
      },
    );
  }

  function onRetry() {
    if (!run) return;
    void startProvision(run.body);
  }

  async function copyLoginUrl() {
    const url =
      reset.data?.login_url ?? `https://app.certainhr.ai/${slug}/login`;
    try {
      await navigator.clipboard.writeText(url);
      setResetCopyState("copied");
      setTimeout(() => setResetCopyState("idle"), 2000);
    } catch {
      // Clipboard API can fail under HTTP origins.
    }
  }

  const inputClass =
    "w-full rounded border border-brand-200 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-surface-canvas dark:text-text-primary";
  const labelClass =
    "mb-1 block text-sm font-medium text-text-secondary dark:text-brand-100/70";

  const loginUrl =
    run?.result?.login_url ?? `https://app.certainhr.ai/${slug}/login`;

  const tenantId = status.data?.tenant_id ?? null;
  const hasTenant = Boolean(status.data?.exists && tenantId);

  return (
    <div className="mx-auto w-full max-w-5xl space-y-4">
      {/* ── HEADER BAND (always visible) ──────────────────────────────────── */}
      <section className="rounded-xl border border-border-default bg-surface-card px-5 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface-subtle text-text-tertiary">
              <Server size={18} aria-hidden />
            </span>
            <div className="min-w-0">
              <p className="text-2xs font-bold uppercase tracking-[0.14em] text-text-tertiary">
                Demo Environment
              </p>
              <h1 className="truncate text-xl font-bold tracking-tight text-text-primary">
                {slug}
              </h1>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            <StatusPill state={headerState} />
            <Button variant="secondary" size="sm" onClick={copyLoginUrl}>
              <LinkIcon size={14} aria-hidden />
              {resetCopyState === "copied" ? "Copied" : "Share login"}
            </Button>
          </div>
        </div>

        {/* Login URL row — lives inside the header band. */}
        <div className="mt-3 flex items-center gap-2 rounded-lg bg-surface-subtle px-3 py-2">
          <span className="shrink-0 text-2xs font-bold uppercase tracking-wide text-text-tertiary">
            Login URL
          </span>
          {headerState === "ready" ? (
            <a
              href={loginUrl}
              target="_blank"
              rel="noreferrer"
              className="truncate text-sm font-medium text-text-link underline decoration-dotted underline-offset-2 hover:decoration-solid"
              data-testid="demo-login-link"
            >
              {loginUrl}
            </a>
          ) : (
            <code
              className="truncate text-sm text-text-tertiary opacity-60"
              data-testid="demo-login-url"
              aria-disabled="true"
              title={
                isProvisioning
                  ? "Available once provisioning completes"
                  : isResetting
                    ? "Available once reset completes"
                    : "Login URL"
              }
            >
              {loginUrl}
            </code>
          )}
          <span className="ml-auto shrink-0">
            <CopyButton
              value={loginUrl}
              ariaLabel="Copy login URL"
              toastLabel="login URL"
            />
          </span>
        </div>
      </section>

      {/* ── Provisioning state-machine banner ─────────────────────────────── */}
      <ProvisioningBanner
        state={headerState === "none" ? null : headerState}
        slug={slug}
        loginUrl={loginUrl}
        candidateCount={status.data?.candidate_count ?? 0}
        rankingCount={status.data?.ranking_count ?? 0}
        errorMessage={run?.error?.message}
        errorCode={run?.error?.error}
        resetElapsed={resetElapsed}
        onRetry={onRetry}
      />

      {/* ── Half-seeded warning ───────────────────────────────────────────────
          The 2026-07-22 failure mode: a reset died partway (its instance was
          replaced by a deploy mid-request), the wipe+provision had already
          committed, and the founder logged into a tenant with 40 of 100
          candidates and 7 of 100 rankings. Nothing on this page said so — the
          counts render fine in isolation. This banner is the tell. It sits
          ABOVE the fold and outranks every other card. */}
      {status.data?.exists &&
      !status.data.seed_complete &&
      headerState !== "provisioning" &&
      headerState !== "failed" &&
      headerState !== "resetting" ? (
        <section
          data-testid="demo-incomplete-seed-banner"
          role="alert"
          className="rounded-xl border-2 border-red-400 bg-red-50 p-4 dark:border-red-500/50 dark:bg-red-500/10"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle
              size={20}
              aria-hidden
              className="mt-0.5 shrink-0 text-red-600 dark:text-red-400"
            />
            <div className="space-y-1">
              <h2 className="text-sm font-semibold text-red-800 dark:text-red-200">
                Incomplete seed — do not demo this tenant
              </h2>
              <p className="text-sm text-red-700 dark:text-red-300">
                Seeding stopped before it finished. This tenant is live and
                login-able but partially built:{" "}
                <strong data-testid="demo-incomplete-candidates">
                  {status.data.candidate_count} of{" "}
                  {status.data.expected_candidate_count}
                </strong>{" "}
                candidates,{" "}
                <strong data-testid="demo-incomplete-rankings">
                  {status.data.ranking_count} of{" "}
                  {status.data.expected_ranking_count}
                </strong>{" "}
                rankings,{" "}
                <strong data-testid="demo-incomplete-jds">
                  {status.data.jd_count} of {status.data.expected_jd_count}
                </strong>{" "}
                JDs.
              </p>
              <p className="text-sm text-red-700 dark:text-red-300">
                Re-run Factory Reset below. It takes ~4 minutes and must not
                overlap a deploy — a merge to <code>main</code> auto-deploys and
                will kill the reset mid-run.
              </p>
            </div>
          </div>
        </section>
      ) : null}

      {/* Hide the operational + danger surfaces while a run is in flight or
          failed — you can't reset/delete a tenant that's still being seeded. */}
      {isProvisioning || isFailed ? null : (
        <>
          {/* ── 3-COL CARD ROW ──────────────────────────────────────────── */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {/* Card 1 — System Metrics */}
            <CollapsibleCard
              title="System Metrics"
              defaultOpen
              leadingIcon={<BarChart3 size={14} aria-hidden />}
            >
              <div data-testid="demo-status-pane">
                {status.isLoading ? (
                  <p className="text-sm text-text-tertiary">Loading…</p>
                ) : status.data?.exists ? (
                  <div className="-mt-1">
                    <MetricRow
                      icon={<Users2 size={14} aria-hidden />}
                      label="Candidates"
                      value={seededValue(
                        status.data.candidate_count,
                        status.data.expected_candidate_count,
                      )}
                    />
                    <MetricRow
                      icon={<ListChecks size={14} aria-hidden />}
                      label="Rankings"
                      value={seededValue(
                        status.data.ranking_count,
                        status.data.expected_ranking_count,
                      )}
                    />
                    <MetricRow
                      icon={<Clock size={14} aria-hidden />}
                      label="Last reset"
                      value={
                        status.data.last_reset_at
                          ? new Date(status.data.last_reset_at).toLocaleString()
                          : "—"
                      }
                    />
                    <MetricRow
                      icon={<Mail size={14} aria-hidden />}
                      label="Admin email"
                      value={status.data.admin_email ?? "—"}
                    />
                  </div>
                ) : (
                  <p className="text-sm text-text-tertiary">
                    No demo tenant at this slug. It may have been deleted.
                  </p>
                )}
              </div>
            </CollapsibleCard>

            {/* Card 2 — Environment Security (MFA toggle) */}
            <CollapsibleCard
              title="Environment Security"
              defaultOpen
              leadingIcon={<ShieldCheck size={14} aria-hidden />}
            >
              {hasTenant && tenantId ? (
                <DemoSecurityCard
                  tenantId={tenantId}
                  mfaRequired={status.data!.mfa_required}
                  onMfaChanged={() => void status.refetch()}
                />
              ) : (
                <p className="text-sm text-text-tertiary">
                  Security controls appear once the demo tenant exists.
                </p>
              )}
            </CollapsibleCard>

            {/* Card 3 — Integration Context (persona) */}
            <CollapsibleCard
              title="Integration Context"
              defaultOpen
              leadingIcon={<Workflow size={14} aria-hidden />}
            >
              {hasTenant && tenantId ? (
                <DemoIntegrationCard
                  tenantId={tenantId}
                  dashboardPersona={status.data!.dashboard_persona}
                  onPersonaChanged={() => void status.refetch()}
                />
              ) : (
                <p className="text-sm text-text-tertiary">
                  Integration context appears once the demo tenant exists.
                </p>
              )}
            </CollapsibleCard>
          </div>

          {/* Reset success card — good news, sits above the Danger Zone. */}
          {reset.data ? (
            <section
              className="rounded-xl border border-status-hired-ring bg-status-hired-bg px-4 py-3 text-sm text-status-hired-fg"
              data-testid="demo-reset-success"
            >
              <p className="font-semibold">Demo tenant reset.</p>
              <p className="mt-1">
                Seeded {reset.data.jds_seeded} JDs,{" "}
                {reset.data.candidates_seeded} candidates,{" "}
                {reset.data.rankings_seeded} rankings.{" "}
                {reset.data.initial_credits} credits granted.
              </p>
              <div className="mt-2 flex items-center gap-2">
                <code className="rounded bg-surface-subtle px-2 py-1 text-xs text-text-primary">
                  {reset.data.login_url}
                </code>
              </div>
            </section>
          ) : null}

          {/* ── USERS (collapsible, default open) ───────────────────────── */}
          {hasTenant && tenantId ? (
            <CollapsibleCard
              title="Users"
              defaultOpen
              leadingIcon={<Users2 size={14} aria-hidden />}
            >
              <div className="mb-3 flex items-center gap-2">
                <div className="relative max-w-xs flex-1">
                  <Search
                    size={14}
                    aria-hidden
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-tertiary"
                  />
                  <input
                    value={userQuery}
                    onChange={(e) => setUserQuery(e.target.value)}
                    placeholder="Search name or email…"
                    className="w-full rounded-lg border border-border-default bg-surface-canvas py-1.5 pl-8 pr-3 text-sm text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--accent-secondary)]"
                    data-testid="demo-user-search"
                  />
                </div>
              </div>
              <TenantUsersTab tenantId={tenantId} searchQuery={userQuery} />
            </CollapsibleCard>
          ) : null}

          {/* ── DANGER ZONE (quarantined) ───────────────────────────────── */}
          <section
            className="rounded-xl border border-status-rejected-ring bg-status-rejected-bg p-4"
            data-testid="demo-danger-zone"
          >
            <div className="mb-3 flex items-center gap-2">
              <AlertTriangle
                size={15}
                aria-hidden
                className="text-status-rejected-fg"
              />
              <h2 className="text-2xs font-bold uppercase tracking-wide text-status-rejected-fg">
                Danger Zone
              </h2>
            </div>

            {/* Row 1 — Factory Reset */}
            <DangerRow
              title="Factory Reset Environment"
              sub="Wipes all data and re-seeds JDs, candidates, and AI rankings. Cannot be undone."
              actionLabel="Reset…"
              open={showResetPanel}
              onReveal={() => setShowResetPanel(true)}
            />
            {showResetPanel ? (
              <form
                onSubmit={onResetSubmit}
                className="mt-3 space-y-3"
                data-testid="demo-reset-form"
              >
                <div>
                  <label className={labelClass}>Admin email</label>
                  <input
                    required
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className={inputClass}
                    data-testid="demo-admin-email"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>First name</label>
                    <input
                      required
                      value={resetFirstName}
                      onChange={(e) => setResetFirstName(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Last name</label>
                    <input
                      required
                      value={resetLastName}
                      onChange={(e) => setResetLastName(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>
                    Type{" "}
                    <code className="rounded bg-brand-100 px-1 dark:bg-white/10">
                      {RESET_TOKEN}
                    </code>{" "}
                    to confirm
                  </label>
                  <input
                    required
                    value={resetConfirm}
                    onChange={(e) => setResetConfirm(e.target.value)}
                    placeholder={RESET_TOKEN}
                    className={inputClass}
                    data-testid="demo-confirm-input"
                  />
                </div>
                {reset.isError ? (
                  <div
                    className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
                    data-testid="demo-reset-error"
                  >
                    <p className="font-medium">
                      {reset.error.status === 409
                        ? "Reset already in progress"
                        : "Reset failed"}
                    </p>
                    <p className="mt-1">{reset.error.message}</p>
                  </div>
                ) : null}
                <div className="flex items-center gap-2">
                  <Button
                    type="submit"
                    variant="destructive"
                    size="sm"
                    disabled={!resetEnabled}
                    data-testid="demo-submit"
                  >
                    {reset.isPending ? "Resetting…" : "Reset demo tenant"}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setShowResetPanel(false);
                      setResetConfirm("");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
                {reset.isPending ? (
                  <p className="text-xs text-text-tertiary">
                    Takes about a minute — the AI ranks each of the 24
                    candidates synchronously.
                  </p>
                ) : null}
              </form>
            ) : null}

            <div className="my-3 border-t border-status-rejected-ring" />

            {/* Row 2 — Terminate */}
            <DangerRow
              title="Terminate Environment"
              sub="Permanently deletes the demo tenant and all its data. Cannot be undone."
              actionLabel="Terminate…"
              open={showDeletePanel}
              onReveal={() => setShowDeletePanel(true)}
              revealTestId="demo-delete-open"
            />
            {showDeletePanel ? (
              <form
                onSubmit={onDeleteSubmit}
                className="mt-3 space-y-3"
                data-testid="demo-delete-form"
              >
                <p className="text-sm text-status-rejected-fg">
                  This permanently deletes the demo tenant and all its data.
                  This cannot be undone.
                </p>
                <div>
                  <label className={labelClass}>
                    Type{" "}
                    <code className="rounded bg-red-100 px-1 dark:bg-red-500/10">
                      {DELETE_TOKEN}
                    </code>{" "}
                    to confirm
                  </label>
                  <input
                    required
                    value={deleteConfirm}
                    onChange={(e) => setDeleteConfirm(e.target.value)}
                    placeholder={DELETE_TOKEN}
                    className={`${inputClass} border-red-300 dark:border-red-500/40`}
                    data-testid="demo-delete-confirm-input"
                  />
                </div>
                {deleteDemo.isError ? (
                  <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
                    <p className="font-medium">Deletion failed</p>
                    <p className="mt-1">{deleteDemo.error.message}</p>
                  </div>
                ) : null}
                <div className="flex items-center gap-2">
                  <Button
                    type="submit"
                    variant="destructive"
                    size="sm"
                    disabled={!deleteEnabled}
                    data-testid="demo-delete-submit"
                  >
                    {deleteDemo.isPending ? "Deleting…" : "Delete demo tenant"}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setShowDeletePanel(false);
                      setDeleteConfirm("");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : null}
          </section>
        </>
      )}
    </div>
  );
}

// ── StatusPill ────────────────────────────────────────────────────────────────

const PILL: Record<HeaderState, string> = {
  ready: "bg-status-hired-bg text-status-hired-fg ring-status-hired-ring",
  provisioning:
    "bg-status-active-bg text-status-active-fg ring-status-active-ring",
  resetting:
    "bg-status-active-bg text-status-active-fg ring-status-active-ring",
  failed:
    "bg-status-rejected-bg text-status-rejected-fg ring-status-rejected-ring",
  none: "bg-surface-subtle text-text-tertiary ring-border-default",
};

const PILL_LABEL: Record<HeaderState, string> = {
  ready: "Ready",
  provisioning: "Provisioning",
  resetting: "Resetting",
  failed: "Failed",
  none: "Not provisioned",
};

function StatusPill({ state }: { state: HeaderState }): JSX.Element {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-2xs font-semibold uppercase tracking-wide ring-1 ring-inset ${PILL[state]}`}
      data-testid="demo-status-pill"
    >
      <span
        className={`h-1.5 w-1.5 rounded-full bg-current ${
          state === "provisioning" || state === "resetting"
            ? "animate-pulse"
            : ""
        }`}
        aria-hidden
      />
      {PILL_LABEL[state]}
    </span>
  );
}

// ── MetricRow ─────────────────────────────────────────────────────────────────

function MetricRow({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3 py-1 text-sm">
      <span className="flex items-center gap-2 text-text-tertiary">
        <span aria-hidden>{icon}</span>
        {label}
      </span>
      <span className="truncate text-right tabular-nums text-text-primary">
        {value}
      </span>
    </div>
  );
}

// ── DangerRow ─────────────────────────────────────────────────────────────────

function DangerRow({
  title,
  sub,
  actionLabel,
  open,
  onReveal,
  revealTestId,
}: {
  title: string;
  sub: string;
  actionLabel: string;
  open: boolean;
  onReveal: () => void;
  revealTestId?: string;
}): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="text-sm font-medium text-text-primary">{title}</p>
        <p className="mt-0.5 text-xs text-text-tertiary">{sub}</p>
      </div>
      {!open ? (
        <Button
          variant="destructive"
          size="sm"
          onClick={onReveal}
          data-testid={revealTestId}
          className="shrink-0"
        >
          {actionLabel}
        </Button>
      ) : null}
    </div>
  );
}

// ── Provisioning banner ───────────────────────────────────────────────────────

interface BannerProps {
  state: DemoProvisionState | "resetting" | null;
  slug: string;
  loginUrl: string;
  candidateCount: number;
  rankingCount: number;
  errorMessage?: string;
  errorCode?: string;
  resetElapsed: number;
  onRetry: () => void;
}

function ProvisioningBanner({
  state,
  loginUrl,
  candidateCount,
  rankingCount,
  errorMessage,
  errorCode,
  resetElapsed,
  onRetry,
}: BannerProps): JSX.Element | null {
  if (state === null) return null;

  if (state === "provisioning") {
    return (
      <section
        className="rounded border border-sky-300 bg-sky-50 px-4 py-3 text-sm text-sky-900 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-200"
        data-testid="demo-provisioning-banner"
        aria-live="polite"
      >
        <div className="flex items-center gap-2">
          <span
            className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-sky-400 border-t-transparent"
            aria-hidden="true"
          />
          <p className="font-semibold">Provisioning…</p>
        </div>
        <p className="mt-1">
          Seeding JDs, candidates, and AI rankings. This takes about a minute —
          you can leave this page; provisioning continues. Seeded so far:{" "}
          {candidateCount} candidates, {rankingCount} rankings.
        </p>
      </section>
    );
  }

  if (state === "resetting") {
    const mins = Math.floor(resetElapsed / 60);
    const secs = resetElapsed % 60;
    const elapsedLabel = `${mins}:${secs.toString().padStart(2, "0")}`;
    return (
      <section
        className="rounded border border-sky-300 bg-sky-50 px-4 py-3 text-sm text-sky-900 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-200"
        data-testid="demo-resetting-banner"
        aria-live="polite"
      >
        <div className="flex items-center gap-2">
          <span
            className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-sky-400 border-t-transparent"
            aria-hidden="true"
          />
          <p className="font-semibold">Resetting…</p>
        </div>
        {/* Indeterminate progress — honestly a pulsing bar, NOT a fake %. */}
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-sky-200 dark:bg-sky-500/20">
          <div className="h-full w-full animate-pulse rounded-full bg-sky-500 dark:bg-sky-400" />
        </div>
        <p className="mt-2">
          Wiping and re-seeding 3 JDs, 24 candidates, and AI rankings. This takes
          about a minute.{" "}
          <strong className="font-semibold">Do not log in until this completes</strong>{" "}
          — the login link is disabled until the reset finishes and is validated.
        </p>
        <p className="mt-1 tabular-nums text-xs text-sky-800/80 dark:text-sky-200/80">
          {elapsedLabel} elapsed · ~60s typical
        </p>
      </section>
    );
  }

  if (state === "failed") {
    return (
      <section
        className="rounded border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200"
        data-testid="demo-provision-failed-banner"
        aria-live="assertive"
      >
        <p className="font-semibold">
          {errorCode === "slug_taken"
            ? "Slug already taken"
            : errorCode === "slug_reserved"
              ? "Slug is reserved"
              : errorCode === "provision_in_progress"
                ? "Provision already in progress"
                : "Provisioning failed"}
        </p>
        <p className="mt-1">
          {errorMessage ??
            "Demo tenant provisioning failed. See SA audit log for the phase."}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 rounded border border-red-400 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 dark:border-red-500/40 dark:bg-red-900/30 dark:text-red-200"
          data-testid="demo-provision-retry"
        >
          Retry
        </button>
      </section>
    );
  }

  // Ready — surface the copy-paste sandbox credentials for sales.
  return (
    <section
      className="rounded border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200"
      data-testid="demo-ready-banner"
      aria-live="polite"
    >
      <p className="font-semibold">Sandbox ready.</p>
      <p className="mt-1">
        Hand the prospect this login. It&apos;s the same fixed demo credential
        for every sandbox — no email is sent and no first-login password change
        is required.
      </p>
      <div
        className="mt-3 space-y-1.5 rounded-lg bg-white/70 p-3 dark:bg-black/20"
        data-testid="demo-share-credentials"
      >
        <CredentialRow label="Login URL" value={loginUrl} copyLabel="login URL" />
        <CredentialRow
          label="Email"
          value={DEMO_ADMIN_EMAIL}
          copyLabel="demo email"
        />
        <div className="flex items-center justify-between gap-3">
          <span className="shrink-0 text-2xs font-bold uppercase tracking-wide text-emerald-700/80 dark:text-emerald-300/70">
            Password
          </span>
          <span className="truncate text-right text-xs text-emerald-800/90 dark:text-emerald-200/80">
            Shared demo password (team vault)
          </span>
        </div>
      </div>
    </section>
  );
}

// ── CredentialRow ─────────────────────────────────────────────────────────────
// One copy-paste line in the "share" block on the Ready banner.

function CredentialRow({
  label,
  value,
  copyLabel,
}: {
  label: string;
  value: string;
  copyLabel: string;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="shrink-0 text-2xs font-bold uppercase tracking-wide text-emerald-700/80 dark:text-emerald-300/70">
        {label}
      </span>
      <span className="flex min-w-0 items-center gap-1">
        <code className="truncate text-xs text-emerald-900 dark:text-emerald-100">
          {value}
        </code>
        <CopyButton value={value} ariaLabel={`Copy ${copyLabel}`} toastLabel={copyLabel} />
      </span>
    </div>
  );
}

// ── Environment Security card body (MFA toggle) ───────────────────────────────
// Parity with the regular-tenant SA surface: the recovery-critical action is
// DISABLE — it clears a stuck demo MFA lockout without a destructive reset.
// Preserves handleToggle + the data-testid + toast strings byte-intact; only
// the trigger UI changed (verb-button → real switch).

function DemoSecurityCard({
  tenantId,
  mfaRequired,
  onMfaChanged,
}: {
  tenantId: string;
  mfaRequired: boolean;
  onMfaChanged: () => void;
}): JSX.Element {
  const addToast = useToastStore((s) => s.addToast);
  const setMfa = useSetTenantMfaRequired(tenantId);

  const handleToggle = () => {
    const next = !mfaRequired;
    setMfa.mutate(next, {
      onSuccess: (r) => {
        addToast(
          r.mfa_required
            ? "Required MFA enabled for this demo tenant."
            : "Required MFA disabled for this demo tenant — users can log in without it.",
          "success",
        );
        onMfaChanged();
      },
      onError: (err) => {
        addToast(
          `Could not change MFA policy: ${err.message || "unknown error"}`,
          "error",
        );
      },
    });
  };

  return (
    <div data-testid="demo-security-users" className="-mt-1">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-text-primary">Required MFA</p>
          <p className="mt-0.5 text-xs text-text-tertiary">
            Disabling clears a stuck MFA lockout immediately — no reset needed.
            This SA control is ungated by the email test-send.
          </p>
          <p className="mt-1 text-xs">
            Status:{" "}
            <span
              className="font-medium text-text-primary"
              data-testid="demo-mfa-status"
            >
              {mfaRequired ? "Required" : "Not required"}
            </span>
          </p>
        </div>
        <Toggle
          checked={mfaRequired}
          pending={setMfa.isPending}
          onChange={handleToggle}
          aria-label="Required MFA"
          data-testid="demo-mfa-toggle"
        />
      </div>
    </div>
  );
}

// ── Integration Context card body (dashboard persona) ─────────────────────────
// Persona setter shipped in #276 (POST /admin/tenants/{id}/dashboard-persona),
// so this stays a fully interactive segmented control — presented per the HoD
// "Integration Context segmented" framing. Logic byte-intact from the prior
// DemoSecurityControls persona block (same hook, testids, toasts, no-op guard).

function DemoIntegrationCard({
  tenantId,
  dashboardPersona,
  onPersonaChanged,
}: {
  tenantId: string;
  dashboardPersona: "agency" | "in_house";
  onPersonaChanged: () => void;
}): JSX.Element {
  const addToast = useToastStore((s) => s.addToast);
  const setPersona = useSetTenantDashboardPersona(tenantId);

  const handlePersonaChange = (next: "agency" | "in_house") => {
    if (next === dashboardPersona) return;
    setPersona.mutate(next, {
      onSuccess: (r) => {
        addToast(
          `Dashboard persona set to ${
            r.dashboard_persona === "agency" ? "Agency" : "In-house TA"
          } for this demo tenant.`,
          "success",
        );
        onPersonaChanged();
      },
      onError: (err) => {
        addToast(
          `Could not change dashboard persona: ${err.message || "unknown error"}`,
          "error",
        );
      },
    });
  };

  return (
    <div className="-mt-1">
      <p className="text-sm font-medium text-text-primary">Persona</p>
      <p className="mt-0.5 text-xs text-text-tertiary">
        Current:{" "}
        <span
          className="font-medium text-text-primary"
          data-testid="demo-persona-status"
        >
          {dashboardPersona === "agency" ? "Agency" : "In-house TA"}
        </span>
      </p>
      <div
        role="radiogroup"
        aria-label="Dashboard persona"
        className="mt-2 inline-flex rounded-lg bg-surface-subtle p-0.5 ring-1 ring-inset ring-border-default"
        data-testid="demo-persona-control"
      >
        {(["agency", "in_house"] as const).map((p) => {
          const active = dashboardPersona === p;
          return (
            <button
              key={p}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => handlePersonaChange(p)}
              disabled={setPersona.isPending}
              className={`rounded-md px-3 py-1 text-xs font-medium transition disabled:opacity-50 ${
                active
                  ? "bg-surface-card text-text-primary shadow-sm"
                  : "text-text-tertiary hover:text-text-primary"
              }`}
              data-testid={`demo-persona-${p}`}
            >
              {p === "agency" ? "Agency" : "In-house TA"}
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-xs text-text-tertiary">
        Shapes seeded copy + nav. The tenant's dashboard renders only this
        variant — there is no in-app toggle.
      </p>
    </div>
  );
}
